import React, { useCallback, useState, useEffect, useRef } from "react";
import { applyEdgeChanges, applyNodeChanges, addEdge } from "@xyflow/react";
import {
  Box,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Typography,
} from "@mui/material";
import Graph from "./Graph";
import NodeConfig from "./NodeConfig";
import NodePalette from "./Components/NodePalette";
import { useAuth } from "../../hooks/AuthProvider";
import {
  NODEBOT_EDGES,
  NODEBOT_NODEBOTS_DETAIL,
  TAG_LIST_ENDPOINT,
  TEMPLATE_ENDPOINT,
} from "../../config/urls";
import { ColorsConstants } from "../../cssUtils/constants";
import { Info } from "@mui/icons-material";

const Flow = ({ workflowId, workflowName }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const reactFlowWrapper = useRef(null);

  useEffect(() => {
    if (workflowId) {
      loadNodebot();
    }
  }, [workflowId]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await fetchTags();
    await fetchTemplates();
  }

  const loadNodebot = async () => {
    setLoading(true);
    try {
      const token = await getValidToken();

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(`${NODEBOT_NODEBOTS_DETAIL}${workflowId}/`, {
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to load flow");
      }

      const data = await response.json();

      // Convert backend nodes to React Flow format.
      // ...node is spread FIRST so the explicit `id` below (stringified) wins
      // over the raw numeric id backend nodes carry — every id comparison
      // elsewhere (handleNodeUpdate, handleNodeDelete, NodeConfig's onUpdate)
      // assumes string ids throughout; the reverse spread order silently
      // clobbered `id` back to a number, which made `node.id ===
      // nodeId.toString()` in handleNodeUpdate always false (number !==
      // string), so a saved config edit never actually reached this
      // component's `nodes` state even though the backend PATCH succeeded.
      const flowNodes = data.nodes.map((node, index) => ({
        ...node,
        id: node.id.toString(),
        type: "custom",
        position: {
          x: 100 + index * 200,
          y: 100 + Math.floor(index / 3) * 150,
        },
        data: {
          label: node.node_type_key.replace("_", " ").toUpperCase(),
          node_type_key: node.node_type_key,
          description: node.config?.description || "",
        },
      }));

      // Convert backend edges to React Flow format — same id-clobbering
      // issue as flowNodes above, fixed the same way.
      const flowEdges = data.edges.map((edge) => ({
        ...edge,
        id: edge.id.toString(),
        source: edge.from_node.toString(),
        target: edge.to_node.toString(),
        type: "smoothstep",
        animated: true,
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load flow");
      showSnackbar("Failed to load flow", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    const token = await getValidToken();

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(`${TAG_LIST_ENDPOINT}`, { headers });

      if (!res.ok) throw new Error("Failed to fetch tags");

      const data = await res.json();

      setTags(data || []);
    } catch (e) {
      console.error("Error fetching tags:", e);
    }
  };

  const fetchTemplates = async () => {
    const token = await getValidToken();

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(
        `${TEMPLATE_ENDPOINT}?limit=1000&ordering=-last_edited`,
        { headers },
      );

      if (!res.ok) throw new Error("Failed to fetch templates");

      const data = await res.json();

      const _templates = data?.results?.filter(
        (template) => template.status === "APPROVED" && template.active,
      );

      setTemplates(_templates || []);
    } catch (e) {
      console.error("Error fetching templates:", e);
    }
  };

  const deleteEdge = async (edgeId) => {
    const token = await getValidToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    const response = await fetch(`${NODEBOT_EDGES}/${edgeId}/delete/`, {
      headers,
      method: "DELETE",
    });

    if (!response.ok) throw new Error("Failed to delete edge");

    const data = await response.json();

    return data;
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleNodeUpdate = (nodeId, config) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId.toString()) {
          return {
            ...node,
            config,
          };
        }
        return node;
      }),
    );

    showSnackbar("Node configuration saved successfully");
  };

  const handleNodeDelete = async (nodeId) => {
    // Delete associated edges first
    const nodesToDelete = edges.filter(
      (edge) =>
        edge.source === nodeId.toString() || edge.target === nodeId.toString(),
    );

    try {
      for (const edge of nodesToDelete) {
        await deleteEdge(parseInt(edge.id));
      }

      setNodes((nds) => nds.filter((node) => node.id !== nodeId.toString()));
      setEdges((eds) =>
        eds.filter(
          (edge) =>
            edge.source !== nodeId.toString() &&
            edge.target !== nodeId.toString(),
        ),
      );
      setSelectedNode(null);

      showSnackbar("Node deleted successfully");
    } catch (err) {
      showSnackbar(
        err.response?.data?.error || "Failed to delete node",
        "error",
      );
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Stack direction="row" sx={{ height: "80vh" }}>
        {/* <NodePalette /> */}

        <Box
          ref={reactFlowWrapper}
          sx={{ position: "relative", flex: 0.75, height: "100%" }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 20,
              left: 20,
              zIndex: 10,
              backgroundColor: "#fff",
              opacity: 0.7,
              p: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {workflowName || "Selected Workflow"}
            </Typography>
          </Box>
          {workflowId ? (
            <Graph
              nodes={nodes}
              edges={edges}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              selectedNode={selectedNode}
              tags={tags}
              templates={templates}
            />
          ) : (
            <Box
              sx={{
                mt: 40,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Info
                sx={{
                  fontSize: 60,
                  color: ColorsConstants.primaryGray40,
                  mb: 2,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: ColorsConstants.primaryGray60,
                }}
              >
                No workflow selected
              </Typography>
            </Box>
          )}
        </Box>

        <NodeConfig
          selectedNode={selectedNode}
          onUpdate={handleNodeUpdate}
          onDelete={handleNodeDelete}
          tags={tags}
          templates={templates}
          nodes={nodes}
        />
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Flow;
